
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ArrowRight, Target, Users, Check, TrendingUp, Calendar, Mail, TicketCheck, CalendarCheck, Gift } from 'lucide-react';

type BannerVariant = 'primary' | 'secondary' | 'highlight';
type BannerSize = 'sm' | 'md' | 'lg';

interface PromotionalBannerProps {
  title: string;
  description?: string;
  benefits?: string[];
  ctaText?: string;
  ctaAction?: () => void;
  variant?: BannerVariant;
  size?: BannerSize;
  className?: string;
  showIcons?: boolean;
  backgroundImage?: string;
}

const PromotionalBanner = ({
  title,
  description,
  benefits = [],
  ctaText = "Próbáld ki most",
  ctaAction,
  variant = 'primary',
  size = 'md',
  className,
  showIcons = true,
  backgroundImage
}: PromotionalBannerProps) => {

  const getVariantStyles = (): string => {
    if (backgroundImage) {
      return 'border-white/20 text-white';
    }

    switch (variant) {
      case 'primary':
        return 'bg-gradient-to-r from-medihub-primary/10 to-medihub-primary/5 border-medihub-primary/20';
      case 'secondary':
        return 'bg-gradient-to-r from-medihub-secondary/10 to-medihub-secondary/5 border-medihub-secondary/20';
      case 'highlight':
        return 'bg-gradient-to-br from-orange-50 to-amber-50 border-amber-200';
      default:
        return 'bg-gradient-to-r from-medihub-primary/10 to-medihub-primary/5 border-medihub-primary/20';
    }
  };

  const getSizeStyles = (): string => {
    switch (size) {
      case 'sm': return 'p-4';
      case 'md': return 'p-6';
      case 'lg': return 'p-8';
      default: return 'p-6';
    }
  };

  const getBenefitIcon = (index: number) => {
    const icons = [
      <TrendingUp key="trending" className={backgroundImage ? "text-medihub-accent" : "text-medihub-primary"} />,
      <Target key="target" className={backgroundImage ? "text-medihub-accent" : "text-medihub-primary"} />,
      <Mail key="mail" className={backgroundImage ? "text-medihub-accent" : "text-medihub-primary"} />,
      <Users key="users" className={backgroundImage ? "text-medihub-accent" : "text-medihub-primary"} />,
      <Calendar key="calendar" className={backgroundImage ? "text-medihub-accent" : "text-medihub-primary"} />,
      <Check key="check" className={backgroundImage ? "text-medihub-accent" : "text-medihub-primary"} />,
      <TicketCheck key="ticket" className={backgroundImage ? "text-medihub-accent" : "text-medihub-primary"} />,
      <CalendarCheck key="calendar-check" className={backgroundImage ? "text-medihub-accent" : "text-medihub-primary"} />,
      <Gift key="gift" className={backgroundImage ? "text-medihub-accent" : "text-medihub-primary"} />
    ];

    return icons[index % icons.length];
  };

  return (
    <div className={cn(
      'rounded-xl border shadow-sm animate-fade-in relative overflow-hidden',
      getVariantStyles(),
      getSizeStyles(),
      className
    )}>
      {backgroundImage && (
        <>
          <div className="absolute inset-0 z-0">
            <img src={backgroundImage} alt="Banner background" className="w-full h-full object-cover" />
          </div>
          <div className="relative z-10">
            <div className="space-y-4">
              <div className="space-y-2">
                {variant === 'highlight' && (
                  <Badge variant="default" className="bg-gradient-to-r from-amber-500 to-orange-500 text-white mb-2">
                    Kiemelt ajánlat
                  </Badge>
                )}

                <h3 className={cn(
                  "font-bold",
                  size === 'lg' ? 'text-2xl' : size === 'md' ? 'text-xl' : 'text-lg'
                )}>
                  {title}
                </h3>

                {description && (
                  <p className={backgroundImage ? "text-white/90" : "text-gray-600"}>{description}</p>
                )}
              </div>

              {benefits.length > 0 && (
                <div className="space-y-2">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start gap-2">
                      {showIcons && (
                        <div className="mt-0.5 h-5 w-5 flex-shrink-0">
                          {getBenefitIcon(index)}
                        </div>
                      )}
                      <span className={backgroundImage ? "text-white/90" : "text-gray-700"}>{benefit}</span>
                    </div>
                  ))}
                </div>
              )}

              {ctaText && (
                <Button
                  onClick={ctaAction}
                  className="mt-2 group bg-medihub-accent text-white hover:bg-medihub-accent/90"
                >
                  {ctaText}
                  <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              )}
            </div>
          </div>
        </>
      )}

      {!backgroundImage && (
        <div className="space-y-4">
          <div className="space-y-2">
            {variant === 'highlight' && (
              <Badge variant="default" className="bg-gradient-to-r from-amber-500 to-orange-500 text-white mb-2">
                Kiemelt ajánlat
              </Badge>
            )}

            <h3 className={cn(
              "font-bold",
              size === 'lg' ? 'text-2xl' : size === 'md' ? 'text-xl' : 'text-lg'
            )}>
              {title}
            </h3>

            {description && (
              <p className="text-gray-600">{description}</p>
            )}
          </div>

          {benefits.length > 0 && (
            <div className="space-y-2">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-2">
                  {showIcons && (
                    <div className="mt-0.5 h-5 w-5 flex-shrink-0">
                      {getBenefitIcon(index)}
                    </div>
                  )}
                  <span className="text-gray-700">{benefit}</span>
                </div>
              ))}
            </div>
          )}

          {ctaText && (
            <Button
              onClick={ctaAction}
              className="mt-2 group"
              variant={variant === 'highlight' ? 'default' : 'outline'}
            >
              {ctaText}
              <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default PromotionalBanner;
